<template>
  <div>
    <div
      class="is-flex is-align-items-center is-justify-content-space-between"
      :class="canUpdateRequest ? 'panel-item--editable' : 'p-bottom-8'"
    >
      <p class="has-text-weight-semibold">Departments</p>
      <NrTooltip v-if="canUpdateRequest" label="Edit departments">
        <NrIconButton
          class="edit-request-assigned-btn"
          :icon="{ name: 'pencil-alt', type: 'regular' }"
          variant="minimal-neutral"
          @click="setCurrentAction('assignDept')"
          aria-label="Edit departments"
        />
      </NrTooltip>
    </div>
    <div class="m-left-8">
      <p
        v-for="department in request.departments"
        :key="department.id"
        class="m-bottom-12"
      >
        <NrTooltip
          v-if="departmentMembersList(department) != ''"
          :label="departmentMembersList(department)"
        >
          {{ department.name }}
        </NrTooltip>
        <span v-else>
          {{ department.name }}
        </span>
      </p>
      <div
        v-if="request.departments.length === 0"
        class="is-italic m-bottom-12"
      >
        <div v-if="canSeeStaffOnlyItems()">No departments assigned</div>
        <div v-else>No departments available</div>
      </div>
    </div>
    <NrModal
      title="Assign departments"
      :show="isAssignDeptModalActive"
      hasSecondaryButton
      :disableButtons="updatingRequestData"
      secondaryButtonText="Cancel"
      primaryButtonText="Assign departments"
      @on-primary-click="updateDepartments"
      @on-secondary-click="setCurrentAction('')"
      @on-close-click="setCurrentAction('')"
    >
      <form class="form" @submit.prevent.stop>
        <div class="field">
          <NrSelect
            v-model="selectedRequestDepartments"
            placeholder="Search and select department"
            :options="filteredDepartments"
            :filterable="false"
            :loading="dataFetchState.departments.fetching"
            :hasMore="!dataFetchState.departments.end"
            @load-more="handleDepartmentScroll"
            @search="filterDepartments"
            @open="filterDepartments"
            @option:selected="selectedDepartment"
            @option:deselected="deselectedDepartment"
            append-to-body
            multiple
          >
            <template v-slot:label>
              <span>Departments</span>
            </template>
          </NrSelect>
          <p v-if="showDepartmentInputHelper" class="help is-danger">
            At least one department is required.
          </p>
        </div>
        <div class="field">
          <NrFormSelect
            label="Request visibility"
            name="visibility"
            v-model="selectedVisibility"
            :options="visibilities"
          />
        </div>

        <div class="field">
          <NrSelect
            v-model="newPoc"
            placeholder="Search and select point of contact"
            :options="pocOptions"
            :filterable="false"
            append-to-body
            @input="newPocSelected"
          >
            <template v-slot:label>
              <span>Assign Point of Contact</span>
            </template>
          </NrSelect>
          <p v-if="showPocAutomationHelperText" class="help">
            Point of Contact has been automatically updated.
          </p>
          <p v-else-if="showPocManualHelperText" class="help">
            Point of Contact has been manually updated.
          </p>
        </div>
      </form>
    </NrModal>
  </div>
</template>
<script lang="ts">
import { mapActions, mapState } from 'vuex'
import { State } from 'src/store/globalModule'
import { throttle, isEmpty } from 'lodash'
import {
  accountRequestVisibilities,
  RequestVisibility
} from '../../../store/globalModule'
import { Request } from '../../request_show/store/request_state_declarations'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrFormSelect from '@nextrequestco/components/src/components/NrFormSelect/nr-form-select'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'

interface Department {
  id: number
  name: string
  poc_id: number // eslint-disable-line camelcase
}

interface Poc {
  id: number
  name: string
  department_name: string // eslint-disable-line camelcase
  department_id: number // eslint-disable-line camelcase
  account_poc?: boolean // eslint-disable-line camelcase
}

interface SelectOption {
  value: number
  label: string
}

interface DepartmentOption {
  value: number
  label: string
  pocId: number
}

const pocOption = ({
  request,
  id = request?.poc?.id,
  emailOrName = request?.poc?.email_or_name,
  accountPocId,
  departments
}: {
  request: Request | null
  id?: number
  emailOrName?: string
  accountPocId?: number
  departments?: string[]
}): SelectOption | Object => {
  if (!request?.poc) {
    return {}
  }

  let descriptionList = []
  if (id === request?.poc?.id) {
    descriptionList.push('Current Point of Contact')
  }

  if (id === accountPocId) {
    descriptionList.push('Account Point of Contact')
  }

  descriptionList = descriptionList.concat(departments)

  return {
    value: id,
    label: `${emailOrName} (${descriptionList.join(', ')})`
  }
}

const initialState = (newRequest: Request | null) => {
  return {
    accountPocOption: null,
    updatingRequestData: false,
    newPoc: pocOption({ request: newRequest }),
    pocOptions: [],
    selectedRequestDepartments:
      newRequest?.departments?.map((dept: Department) => ({
        value: dept.id,
        label: dept.name,
        pocId: dept.poc_id
      })) || [],
    selectedVisibility: newRequest?.visibility || '',
    showPocAutomationHelperText: false,
    showPocManualHelperText: false
  }
}

export default {
  components: {
    NrModal,
    NrSelect,
    NrFormSelect,
    NrTooltip,
    NrIconButton
  },
  data() {
    return initialState(null)
  },
  watch: {
    currentAction(next, _prev) {
      if (next === 'assignDept') {
        this.resetData(this.request)
        this.updatePocOptions()
      }
    },
    request(newRequest) {
      // resets all dropdown data while cycling requests
      this.resetData(newRequest)
    }
  },
  computed: {
    ...mapState(['account']),

    ...mapState({
      departmentRequired: (state: State): boolean => {
        return !!state.account?.require_department
      }
    }),

    ...mapState('request_show', [
      'currentAction',
      'request',
      'dataFetchState',
      'dataOptions',
      'requestStaff'
    ]),

    canUpdateRequest(): boolean {
      return this.$ability.can('manage', 'Request')
    },

    visibilities(): RequestVisibility[] {
      return accountRequestVisibilities(this.account)
    },

    showDepartmentInputHelper(): boolean {
      return (
        this.departmentRequired && this.selectedRequestDepartments.length < 1
      )
    },

    isAssignDeptModalActive(): boolean {
      return this.currentAction === 'assignDept'
    },

    filteredDepartments(): DepartmentOption[] {
      return this.dataOptions.departments.map((department: Department) => ({
        label: department.name,
        value: department.id,
        pocId: department.poc_id
      }))
    }
  },
  methods: {
    ...mapActions('request_show', [
      'fetchDepartmentPocs',
      'fetchRequestStaff',
      'fetchDataOptions',
      'fetchTimeline',
      'fetchRequest',
      'updateRequest',
      'setCurrentAction'
    ]),

    resetData(newRequest: Request) {
      Object.assign(this.$data, initialState(newRequest))
    },

    applyPocAutomations() {
      // Returns early if account poc automation is turned off
      if (!this.account.poc_automation) {
        return
      }
      const currentPoc = this.newPoc?.value
      // Automatically select the PoC of the first dept assigned to the request,
      // unless the currently selected PoC is assigned to a different selected dept.
      if (
        this.selectedRequestDepartments.length >= 1 &&
        (!this.newPoc || this.newPoc?.value === this.accountPocOption?.value)
      ) {
        const firstDeptPoc = this.pocOptions.find((option: SelectOption) => {
          return option.value === this.selectedRequestDepartments[0].pocId
        })
        if (!firstDeptPoc) return
        this.newPoc = firstDeptPoc
      }

      // Automatically select the Account PoC when all departments are removed.
      // (Even if the actual current PoC is different.)
      if (this.selectedRequestDepartments.length === 0) {
        this.newPoc = this.accountPocOption
      }

      const newPoc = this.newPoc?.value
      // Show poc automation helper text when POC changes
      if (newPoc !== currentPoc) {
        this.showPocAutomationHelperText = true
      } else {
        this.showPocAutomationHelperText = false
      }
      this.showPocManualHelperText = false
    },

    newPocSelected() {
      this.showPocManualHelperText = true
      this.showPocAutomationHelperText = false
    },

    departmentMembersList(department: Department) {
      const membersList = []
      let pocFound = false
      this.requestStaff.forEach((staff) => {
        if (staff.id === department.poc_id) {
          membersList.unshift(staff.name_or_email + ' (Point of Contact)')
          pocFound = true
        } else if (staff.departments.includes(department.name)) {
          membersList.push(staff.name_or_email)
        }
      })
      if (!pocFound) {
        membersList.unshift(
          this.request.poc.email_or_name + ' (Point of Contact)'
        )
      }
      return membersList.join(', ')
    },

    selectedDepartment() {
      this.updatePocOptions().then(() => {
        this.applyPocAutomations()
      })
    },

    deselectedDepartment() {
      this.updatePocOptions().then(() => {
        this.applyPocAutomations()
      })
    },

    filterDepartments: throttle(function (searchText = '') {
      this.fetchDataOptions({ searchText, dataType: 'departments' })
    }, 400),

    handleDepartmentScroll: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.departments.search_text,
        dataType: 'departments'
      })
    }, 400),

    setAssignedRequestDepartments() {
      this.selectedRequestDepartments = this.request.departments?.map(
        (dept: Department) => ({
          value: dept.id,
          label: dept.name,
          pocId: dept.poc_id
        })
      )
    },

    setAssignedRequestVisibility() {
      this.selectedVisibility = this.request.visibility
    },

    findPocByDepartment(pocs: Poc[], departmentOption: DepartmentOption) {
      const deptPocId = departmentOption.pocId
      return pocs.find((poc: Poc) => poc.id === deptPocId)
    },

    updatePocOptions() {
      return this.fetchDepartmentPocs({
        departmentIds: this.selectedRequestDepartments.map(
          (dept: SelectOption) => dept.value
        ),
        pocId: this.request.poc.id
      }).then((pocs: Poc[]) => {
        // Fixes a type error where `concat` is not defined when the list has no entries.
        if (!pocs.length) {
          pocs = [] as Poc[]
        }

        // Ensures an entry for the current PoC is populated even if the current
        // PoC is not the PoC of any departments.
        const pocsWithCurrent = pocs.concat([
          {
            id: this.request.poc.id,
            name: this.request.poc.name_or_email,
            department_name: '',
            department_id: -1
          } as Poc
        ])

        const options = {}
        pocsWithCurrent.forEach((poc: Poc) => {
          if (!options[poc.id]) {
            options[poc.id] = pocOption({
              id: poc.id,
              emailOrName: poc.name,
              request: this.request,
              accountPocId: this.account.poc_id,
              departments: pocs
                .filter((entry) => entry.id === poc.id && !entry.account_poc)
                .map((entry) => entry.department_name)
            })
            if (poc.account_poc) {
              this.accountPocOption = options[poc.id]
            }
          }
        })

        // Automatically update the selected PoC's description text,
        // when it is still a valid value. (This is relevant when the same user
        // is assigned as the PoC of multiple departments, and one of their
        // departments is added/removed.)
        if (this.newPoc?.value) {
          this.newPoc = options[this.newPoc.value]
        }

        this.pocOptions = Object.values(options)
      })
    },

    updateDepartments() {
      this.updatingRequestData = true
      this.updateRequest({
        request_id: this.request.pretty_id,
        department_ids: this.selectedRequestDepartments.map(
          ({ value }) => value
        ),
        visibility: this.selectedVisibility,
        poc_id: !isEmpty(this.newPoc) ? this.newPoc.value : undefined
      })
        .then(() => {
          this.fetchRequest(this.request.pretty_id)
          this.setCurrentAction('')
          this.$toasted.success('Departments updated successfully')
          this.fetchTimeline({
            pretty_id: this.request.pretty_id
          })
          this.fetchRequestStaff({ request_id: this.request.pretty_id }).catch(
            () => {
              this.$toasted.error(
                'There was an error retrieving the request staff'
              )
            }
          )
        })
        .catch((_error: Error) => {
          this.$toasted.error('Departments updated failed')
        })
        .finally(() => {
          this.updatingRequestData = false
        })
    },
    canSeeStaffOnlyItems() {
      return this.$ability.can('staff_only_items')
    }
  }
}
</script>
